/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import {
    IProFormaUses, ISelectedProjection, ProjectionType, DevelopmentUnitOfMeasureTypes,
    IBudgetClassificationPatchData, FinancingFeeCalculatedOn, IFormattedCashFlowPeriod,
} from 'views/ProFormaTable/types';
import { numeralFormatterCurrency, formatISOStartOfMonth } from 'ns_libs/formatter';
import RenderIf from 'components/RenderIf/RenderIf';
import { URLS } from 'services/urls';
import {
    BudgetClassificationTypeEnum, EDITABLE_BUDGET_CLASSIFICATION_TYPES, PROJECTION_TYPE, PROJECTION_TYPES,
} from 'views/ProFormaTable/constants';
import { PRO_FORMA_UNIT_TO_FIELD_VALUES } from 'constants/unitTypes';
import NSCellInput from 'bricks/NSCellInput/NSCellInput';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { useProFormaSocketContext } from 'views/ProFormaTable/components/ProForma/socketContext/ProFormaSocketProvider';
import { formatCashFlowPeriods, getPeriodsOptions } from 'views/ProFormaTable/helpers';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import PeriodsSelect from '../../../../../PeriodsSelect/PeriodsSelect';
import CurveFitModal from '../../../../CurveFitModal/CurveFitModal';
import { useUsesTableContext } from '../../../context/UsesTableProvider';
import PeriodCellValue from '../../../PeriodCellValue/PeriodCellValue';
import UsesTableBudgetLineNameCell from './UsesTableBudgetLineNameCell/UsesTableBudgetLineNameCell';

export interface IUsesTableBudgetLine {
    budgetClassification: IProFormaUses;
    unitType: DevelopmentUnitOfMeasureTypes;
    projectionPeriods: IFormattedCashFlowPeriod[];
    isPreview?: boolean;
}

const UsesTableBudgetLine = ({
    budgetClassification, unitType, isPreview, projectionPeriods,
}: IUsesTableBudgetLine) => {
    const {
        proForma,
        handleUpdateBudgetClassifications,
        isBudgetClassificationUpdatingLoading,
        handleDeleteBudgetClassification,
        isBudgetClassificationDeletionLoading,
        showCents,
        cashFlowPeriodsByCadence,
        periodCadence,
    } = useProFormaSocketContext();

    const { selectedCategories } = useUsesTableContext();
    const [isHovered, setIsHovered] = useState(false);
    const [curveFitModalOpen, setCurveFitModalOpen] = useState(false);
    const [openDeleteBudgetLineModal, setOpenDeleteBudgetLineModal] = useState(false);

    const selectedUnitTotal = budgetClassification[PRO_FORMA_UNIT_TO_FIELD_VALUES[unitType]];

    const toggleDeleteBudgetLineModal = () => setOpenDeleteBudgetLineModal(!openDeleteBudgetLineModal);
    const toggleCurveFitModal = () => setCurveFitModalOpen(!curveFitModalOpen);

    const selectedProjectionsCurveList = PROJECTION_TYPES.filter(projection => projection.projectionType === budgetClassification.projectionType);
    const selectedCurveByVariable = selectedProjectionsCurveList?.find(
        projection => projection.projectionVariable === Number(budgetClassification.projectionVariable),
    );

    let selectedCurve: ISelectedProjection = {
        name: selectedProjectionsCurveList[0]?.curveType,
        value: budgetClassification.projectionVariable || null,
        imgPrimary: selectedProjectionsCurveList[0]?.imgPrimary,
    };

    if (
        budgetClassification.projectionType
        && [PROJECTION_TYPE.NORMAL, PROJECTION_TYPE.GAMMA].includes(budgetClassification.projectionType)
        && selectedCurveByVariable
    ) {
        selectedCurve = {
            name: selectedCurveByVariable.curveType,
            value: budgetClassification.projectionVariable || null,
            imgPrimary: selectedCurveByVariable.imgPrimary,
        };
    }

    const updateSuccessCallback = () => {
        if (curveFitModalOpen) toggleCurveFitModal();
    };

    const updateBudgetClassification = (updatedValues: Partial<IBudgetClassificationPatchData>) => {
        if (budgetClassification.isDeletable || EDITABLE_BUDGET_CLASSIFICATION_TYPES.includes(budgetClassification.classificationType)
            || 'leaseUpIncomePercentage' in updatedValues
        ) {
            const values = { ...updatedValues };
            if ('percentageOfDebt' in values) {
                let value = Number(values.percentageOfDebt);
                value = Math.min(value, 1);
                values.percentageOfDebt = String(value);
                values.calculatedOn = 'percentage_of_debt' as FinancingFeeCalculatedOn;
            }
            if ('leaseUpIncomePercentage' in values) {
                let value = Number(values.leaseUpIncomePercentage);

                value = Math.min(value, 1);
                values.leaseUpIncomePercentage = String(value);
            }
            handleUpdateBudgetClassifications([{ id: budgetClassification.id, ...values }], updateSuccessCallback);
        }
    };

    const handleUpdateProjection = (projectionType: ProjectionType, projectionVariable: number | null) => {
        const targetProjection = PROJECTION_TYPES.find(projection => projection.projectionType === projectionType);
        if (targetProjection) {
            updateBudgetClassification({
                projectionType,
                projectionVariable,
            });
        }
    };

    const deleteBudgetLine = () => {
        handleDeleteBudgetClassification([budgetClassification.id], false, toggleDeleteBudgetLineModal);
    };

    const monthlyPeriodOptions = formatCashFlowPeriods(cashFlowPeriodsByCadence, Periods.MONTHLY);

    const periodOptions = getPeriodsOptions(proForma.startDate, proForma.saleDate, monthlyPeriodOptions);

    const hasNoSelectedCategories = Object.values(selectedCategories).every(categoryLevel => !categoryLevel.length);

    let budgetClassificationPeriods = budgetClassification.periods || [];
    if (periodCadence === Periods.ANNUALLY) budgetClassificationPeriods = budgetClassification.yearlyPeriods || [];

    const isInterestReserve = budgetClassification.classificationType === BudgetClassificationTypeEnum.INTEREST_RESERVE;
    const isOperatingReserve = budgetClassification.classificationType === BudgetClassificationTypeEnum.OPERATING_RESERVE;
    const isUneditableBudgetClassification = isInterestReserve || isOperatingReserve;

    return (
        <>
            <tr className="NSTable__tbody__tr NSTable__tbody__tr--level-3">
                <td
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border NSTable__tbody__tr__td__input h-100"
                >
                    <div className="d-flex justify-content-between align-items-center">
                        <UsesTableBudgetLineNameCell
                            budgetClassification={budgetClassification}
                            updateBudgetClassification={updateBudgetClassification}
                            isPreview={isPreview}

                        />
                        <RenderIf isTrue={hasNoSelectedCategories && budgetClassification.isDeletable}>
                            <FontAwesomeIcon
                                icon={faTrash}
                                onClick={() => isHovered && toggleDeleteBudgetLineModal()}
                                className={classNames('text-muted cursor--pointer pr-2 hover:text-dark', { invisible: !isHovered })}
                            />
                        </RenderIf>
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td__input">
                    <RenderIf isTrue={budgetClassification.isDeletable
                        || EDITABLE_BUDGET_CLASSIFICATION_TYPES.includes(budgetClassification.classificationType)}
                    >
                        <PeriodsSelect
                            handleChange={value => updateBudgetClassification({ startDate: value })}
                            options={periodOptions}
                            value={!budgetClassification.startDate ? null
                                : formatISOStartOfMonth(budgetClassification.startDate)}
                        />
                    </RenderIf>
                </td>
                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td__input">
                    <RenderIf isTrue={budgetClassification.isDeletable
                        || EDITABLE_BUDGET_CLASSIFICATION_TYPES.includes(budgetClassification.classificationType)}
                    >
                        <PeriodsSelect
                            handleChange={value => updateBudgetClassification({ endDate: value })}
                            options={periodOptions}
                            value={!budgetClassification.endDate ? null
                                : formatISOStartOfMonth(budgetClassification.endDate)}
                        />
                    </RenderIf>
                </td>
                <td
                    className="NSTable__tbody__tr__td align-middle text-primary cursor--pointer text-primary text-nowrap"
                    onClick={toggleCurveFitModal}
                >
                    <RenderIf isTrue={budgetClassification.isDeletable
                        || EDITABLE_BUDGET_CLASSIFICATION_TYPES.includes(budgetClassification.classificationType)}
                    >
                        <RenderIf isTrue={budgetClassification.projectionType}>
                            <img
                                src={URLS.IMAGE_CLOUDFRONT_URL(selectedCurve.imgPrimary)}
                                alt={`selectedCurve-${selectedCurve.name}`}
                                width="16px"
                                className="mx-auto"
                            />
                            <small className="mt-0 ml-1">{selectedCurve.name}</small>
                        </RenderIf>
                        <RenderIf isTrue={!budgetClassification.projectionType}>
                            <div className="text-primary text-nowrap font-italic">+ Add</div>
                        </RenderIf>
                    </RenderIf>
                </td>
                <td className="NSTable__tbody__tr__td align-middle">
                    <div className="d-flex justify-content-end text-dark">
                        {selectedUnitTotal != null ? numeralFormatterCurrency(selectedUnitTotal) : '—'}
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border
                NSTable__tbody__tr__td__input NSTable__tbody__tr__td--width-100"
                >
                    <div className="d-flex align-items-center justify-content-end">
                        <NSCellInput
                            type="number"
                            numberType="currency"
                            value={budgetClassification.amount}
                            handleUpdateValue={updatedValue => {
                                if (budgetClassification.classificationType === BudgetClassificationTypeEnum.FINANCING_FEE) {
                                    updateBudgetClassification({ amount: String(updatedValue), calculatedOn: 'amount' as FinancingFeeCalculatedOn });
                                } else {
                                    updateBudgetClassification({ amount: String(updatedValue) });
                                }
                            }}
                            placeholder={isUneditableBudgetClassification ? '—' : 'Enter amount'}
                            isRightAligned
                            customClassName={classNames('mr-1', {
                                'text-dark ': isUneditableBudgetClassification,
                            })}
                            disabled={isUneditableBudgetClassification}
                            showCents={showCents}
                        />
                    </div>
                </td>
                {projectionPeriods?.map((period, index) => {
                    const targetPeriod = budgetClassificationPeriods[index];

                    return (
                        <PeriodCellValue
                            key={`budget-line-period-${index}`}
                            tooltipId={`budget-line-period-${index}`}
                            period={period}
                            value={targetPeriod?.projected === null || budgetClassification.amount === null
                                ? '—' : numeralFormatterCurrency(targetPeriod?.projected, showCents ? '$0,0.00' : '$0,0')}
                            cellClassName="NSTable__tbody__tr__td NSTable__tbody__tr__td--width-100 text-dark text-right"
                        />
                    );
                })}
            </tr>

            <RenderIf isTrue={curveFitModalOpen}>
                <CurveFitModal
                    toggle={toggleCurveFitModal}
                    isOpen={curveFitModalOpen}
                    headerText="Curve"
                    handleUpdateProjection={handleUpdateProjection}
                    isLoadingCallback={isBudgetClassificationUpdatingLoading}
                    selectedCurve={selectedCurveByVariable}
                    projectionVariable={budgetClassification?.projectionVariable}
                />
            </RenderIf>

            <RenderIf isTrue={openDeleteBudgetLineModal}>
                <ConfirmationModal
                    toggle={toggleDeleteBudgetLineModal}
                    isOpen={openDeleteBudgetLineModal}
                    onConfirm={deleteBudgetLine}
                    onCancel={toggleDeleteBudgetLineModal}
                    modalHeaderText={`Delete ${budgetClassification.budgetLineNickname}`}
                    modalFooterButtonText="Yes, delete"
                    modalFooterButtonIsLoading={isBudgetClassificationDeletionLoading}
                    danger
                >
                    <div className="text-dark font-weight-bold mb-1">
                        {`Are you sure you want to delete ${budgetClassification.budgetLineNickname}?`}
                    </div>
                    <div>Your work will not be able to be recovered.</div>
                </ConfirmationModal>
            </RenderIf>
        </>
    );
};

export default UsesTableBudgetLine;
